import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LRUCache } from "lru-cache";
import api from "utils/api";
import { useRequest } from "hooks/useRequest";
import { useLatest } from "hooks/useLatest";
import { useSearchParamState } from "hooks/useSearchParamState";
import { fetchSingleDiagram } from "../../utils/fetchSingleDiagram";
import { SIGNED_URL_SAFE_TTL_MS } from "../../utils/signedAssetUrls";
import { resolveDiagramRecordIndex } from "./utils";
import type { BoardDiagramV3, DiagramRecord } from "../../utils/types";
import type {
  PatchCachedDiagramResult,
  UseLazyDiagramLoaderArgs,
  UseLazyDiagramLoaderResult,
} from "./types";

export const useLazyDiagramLoader = ({
  projectId,
}: UseLazyDiagramLoaderArgs): UseLazyDiagramLoaderResult => {
  const [diagramIdParam, setDiagramIdParam] = useSearchParamState<
    number | null
  >({
    key: "diagramId",
    defaultValue: null,
    parse: Number,
    serialize: (value) => (value === null ? "" : String(value)),
    replace: true,
  });

  const [selectedDiagram, setSelectedDiagram] = useState<BoardDiagramV3 | null>(
    null,
  );

  const [pendingDiagramRecordIndex, setPendingDiagramRecordIndex] = useState<
    number | null
  >(null);

  // TTL: a diagram's signed asset URLs expire, so a stale entry is a cache
  // miss — this also makes loadAllDiagrams re-preload stale entries for Karl.
  const diagramCacheRef = useRef(
    new LRUCache<number, BoardDiagramV3>({
      max: 50,
      ttl: SIGNED_URL_SAFE_TTL_MS,
    }),
  );
  // The cache is a ref, so nothing re-renders when it fills. Karl's schematic
  // lookup has to see every diagram that has arrived, so mirror it in state.
  const [loadedDiagrams, setLoadedDiagrams] = useState<
    Record<number, BoardDiagramV3>
  >({});
  const requestCounterRef = useRef(0);
  const preloadingRef = useRef<Set<number>>(new Set());
  const diagramIdParamRef = useLatest(diagramIdParam);

  const { data: diagramRecords = [], isLoading: isInitialLoading } = useRequest<
    DiagramRecord[],
    []
  >(
    async () => {
      const records = await api.getQuantitySurveyorDiagramsByProject(projectId);

      const initialIndex = resolveDiagramRecordIndex(
        records,
        diagramIdParamRef.current,
      );
      const record = records[initialIndex];
      if (record) {
        try {
          const diagram = await fetchSingleDiagram({ diagramRecord: record });
          if (diagram) {
            diagramCacheRef.current.set(record.id, diagram);
          }
          setSelectedDiagram(diagram);
        } catch {
          // Still expose the records if the initial diagram fails; the effect
          // below retries on the cache miss.
        }
      }

      // Returned last: `data` is exposed only after the cache is warmed, so
      // the effect below hits the cache instead of refetching.
      return records;
    },
    { manual: false, defaultParams: [] },
  );

  const selectedDiagramRecordIndex = useMemo(
    () => resolveDiagramRecordIndex(diagramRecords, diagramIdParam),
    [diagramRecords, diagramIdParam],
  );

  const selectDiagramRecord = useCallback(
    (recordIndex: number) => {
      const record = diagramRecords[recordIndex];
      if (!record) return;
      setDiagramIdParam(record.id);
    },
    [diagramRecords, setDiagramIdParam],
  );

  const updateCachedDiagram = useCallback(
    (
      diagramId: number,
      updater: (diagram: BoardDiagramV3) => BoardDiagramV3,
    ) => {
      const cached = diagramCacheRef.current.get(diagramId);
      const updated = cached ? updater(cached) : null;
      if (updated) {
        // The URLs inside are unchanged, so the entry keeps its original TTL.
        diagramCacheRef.current.set(diagramId, updated, {
          noUpdateTTL: true,
        });
      }

      // Karl searches this mirror too, so an edit must not leave the old diagram in it.
      setLoadedDiagrams((held) => {
        const loaded = held[diagramId];
        if (loaded === undefined) return held;
        return { ...held, [diagramId]: updated ?? updater(loaded) };
      });

      // A TTL-expired entry misses the cache write above, but the edit must
      // still reach the diagram on screen.
      setSelectedDiagram((prev) => {
        if (
          prev &&
          diagramRecords[selectedDiagramRecordIndex]?.id === diagramId
        ) {
          return diagramCacheRef.current.get(diagramId) ?? updater(prev);
        }
        return prev;
      });
    },
    [diagramRecords, selectedDiagramRecordIndex],
  );

  const reloadCachedDiagram = useCallback(
    async (diagramId: number) => {
      const record = diagramRecords.find((item) => item.id === diagramId);
      if (!record) return;

      // Leaves the selection counter alone, so a diagram being opened meanwhile still finishes loading.
      try {
        const diagram = await fetchSingleDiagram({ diagramRecord: record });
        if (!diagram) return;
        diagramCacheRef.current.set(diagramId, diagram);
        setLoadedDiagrams((held) => ({ ...held, [diagramId]: diagram }));
        setSelectedDiagram((prev) =>
          prev?.diagram.id === diagramId ? diagram : prev,
        );
      } catch {
        // The stale diagram stays visible; the next selection refetches it.
      }
    },
    [diagramRecords],
  );

  const patchCachedDiagram = useCallback(
    (
      diagramId: number,
      patcher: (diagram: BoardDiagramV3) => PatchCachedDiagramResult,
    ) => {
      const current =
        diagramCacheRef.current.get(diagramId) ??
        (selectedDiagram?.diagram.id === diagramId ? selectedDiagram : null);
      if (current === null) return;

      const result = patcher(current);
      if (result.kind === "reload") {
        reloadCachedDiagram(diagramId);
        return;
      }
      updateCachedDiagram(diagramId, () => result.diagram);
    },
    [reloadCachedDiagram, updateCachedDiagram, selectedDiagram],
  );

  // A designator lives in one schematic, and the estimator may be looking at
  // another. Karl searches them all, so they all have to be here.
  const loadAllDiagrams = useCallback(() => {
    for (const record of diagramRecords) {
      if (diagramCacheRef.current.has(record.id)) continue;
      // A separate set, never the cache: the selection effect reads the cache
      // and would happily render a half-loaded placeholder as the diagram.
      if (preloadingRef.current.has(record.id)) continue;
      preloadingRef.current.add(record.id);
      void (async () => {
        try {
          const diagram = await fetchSingleDiagram({ diagramRecord: record });
          if (!diagram) return;
          diagramCacheRef.current.set(record.id, diagram);
          setLoadedDiagrams((held) => ({ ...held, [record.id]: diagram }));
        } catch {
          // A schematic that will not load is one Karl cannot point into.
        } finally {
          preloadingRef.current.delete(record.id);
        }
      })();
    }
  }, [diagramRecords]);

  useEffect(() => {
    const record = diagramRecords[selectedDiagramRecordIndex];
    if (!record) return;

    // Invalidate any in-flight fetch so a slower earlier selection can't
    // overwrite this one (e.g. selecting a cached diagram mid-load).
    const requestId = ++requestCounterRef.current;

    const cached = diagramCacheRef.current.get(record.id);
    if (cached) {
      setSelectedDiagram(cached);
      setPendingDiagramRecordIndex(null);
      return;
    }

    setPendingDiagramRecordIndex(selectedDiagramRecordIndex);

    (async () => {
      try {
        const diagram = await fetchSingleDiagram({ diagramRecord: record });
        if (requestId !== requestCounterRef.current) return;
        if (diagram) {
          diagramCacheRef.current.set(record.id, diagram);
          setLoadedDiagrams((held) => ({ ...held, [record.id]: diagram }));
        }
        setSelectedDiagram(diagram);
      } catch {
        // Keep previous diagram data visible on error
      } finally {
        if (requestId === requestCounterRef.current) {
          setPendingDiagramRecordIndex(null);
        }
      }
    })();
  }, [diagramRecords, selectedDiagramRecordIndex]);

  return {
    isInitialLoading,
    diagramRecords,
    selectedDiagramRecordIndex,
    pendingDiagramRecordIndex,
    selectedDiagram,
    selectDiagramRecord,
    loadedDiagrams,
    loadAllDiagrams,
    updateCachedDiagram,
    patchCachedDiagram,
  };
};
